import { inject, Injectable, signal } from '@angular/core';
import { ICustomizationFilters } from '@models/customization.types';
import {
    ArchiveEntityOptionView,
    CustomFieldTypeView,
    EntityTypeCustomFieldView,
    ReserveEntityOptionView
} from './api-proxies';
import { ArchiveEntityService } from './archive-entity.service';
import { CustomFieldTypeService } from './custom-field-type.service';
import { EntityTypeService } from './entity-type.service';

@Injectable({
    providedIn: 'root'
})
export class CustomizationService {
    private readonly archiveOptions = signal<ArchiveEntityOptionView[] | undefined>(undefined);
    private readonly reservedOptions = signal<ReserveEntityOptionView[] | undefined>(undefined);
    private readonly customFieldTypes = signal<CustomFieldTypeView[] | undefined>(undefined);

    private archiveOptionsPromise: Promise<ArchiveEntityOptionView[]> | null = null;
    private reservedOptionsPromise: Promise<ReserveEntityOptionView[]> | null = null;
    private customFieldTypesPromise: Promise<CustomFieldTypeView[]> | null = null;

    private readonly archiveEntityService = inject(ArchiveEntityService);
    private readonly entityTypeService = inject(EntityTypeService);
    private readonly customFieldTypeService = inject(CustomFieldTypeService);

    async getArchiveOptions(): Promise<ArchiveEntityOptionView[]> {
        const cached = this.archiveOptions();
        if (cached) return cached;

        if (this.archiveOptionsPromise) {
            return this.archiveOptionsPromise;
        }

        this.archiveOptionsPromise = this.archiveEntityService.getArchiveOptions().then((options) => {
            this.archiveOptions.set(options);
            this.archiveOptionsPromise = null;
            return options;
        });

        return this.archiveOptionsPromise;
    }

    async getReservedOptions(): Promise<ReserveEntityOptionView[]> {
        const cached = this.reservedOptions();
        if (cached) return cached;

        if (this.reservedOptionsPromise) {
            return this.reservedOptionsPromise;
        }

        this.reservedOptionsPromise = this.entityTypeService.getReservedOptions().then((options) => {
            this.reservedOptions.set(options);
            this.reservedOptionsPromise = null;
            return options;
        });

        return this.reservedOptionsPromise;
    }

    async getCustomFieldTypes(): Promise<CustomFieldTypeView[]> {
        const cached = this.customFieldTypes();
        if (cached) return cached;

        if (this.customFieldTypesPromise) {
            return this.customFieldTypesPromise;
        }

        this.customFieldTypesPromise = this.customFieldTypeService.getAll().then((types) => {
            this.customFieldTypes.set(types);
            this.customFieldTypesPromise = null;
            return types;
        });

        return this.customFieldTypesPromise;
    }

    canAddNew(filters: ICustomizationFilters): boolean {
        return filters.status?.value !== false && filters.source?.value !== true;
    }

    prepareFieldsForSave(fields: EntityTypeCustomFieldView[]): EntityTypeCustomFieldView[] {
        return fields.map((field) => {
            return field.id
                ? { ...field }
                : ({
                      entityTypeId: field.entityTypeId,
                      customFieldTypeId: field.customFieldTypeId,
                      entityTypeCustomFieldSectionId: field.entityTypeCustomFieldSectionId,
                      isActive: field.isActive,
                      name: field.name,
                      description: field.description,
                      options: field.options
                  } as EntityTypeCustomFieldView);
        });
    }

    areFieldsEqual(current: EntityTypeCustomFieldView[], reference: EntityTypeCustomFieldView[]): boolean {
        return JSON.stringify(current ?? []) === JSON.stringify(reference ?? []);
    }

    areInfoEqual<T = unknown>(current: T[], reference: T[]): boolean {
        return JSON.stringify(current ?? []) === JSON.stringify(reference ?? []);
    }
}
